import CustomButton from "../../../components/Button";
import DownArrowIcon from "../../../assets/Filter/downArrow.svg";
import UpArrowIcon from "../../../assets/Filter/upArrow.svg";
import RupeeIcon from "../../../assets/common/Rupee.svg";

import { useEffect, useRef, useState } from "react";
import Collapsible from "react-collapsible";

import { createColumnHelper } from "@tanstack/react-table";
import {
  date_DD_MMM_YYY,
  date_DD_MMM_YYYY_HH_MM,
} from "../../../utils/dateFormater";
import bookedIcon from "../../../assets/Transaction/bookedIcon.svg";
import cancelledicon from "../../../assets/Transaction/cancelledIcon.svg";
import CopyTooltip from "../../../components/CopyToClipboard";
import PendingIcon from "../../../assets/pendingRed.svg";
import filterIconTable from "../../../assets/Transaction/filtericon.svg";
import { COMPANY_NAME } from "../../../utils/ApiUrls";
import { capitalizeFirstLetter } from "../../../utils/utility";

export type WalletApiSortKey = "_id" | "balance" | "credited" | "debited";

export interface WalletApiSortState {
  key: WalletApiSortKey;
  dir: 1 | -1;
}

export const DEFAULT_WALLET_API_SORT: WalletApiSortState = {
  key: "_id",
  dir: -1,
};

export interface WalletApiSortPayloadFields {
  amountKey?: string;
  balanceKey?: string;
}

export function walletTxSortToPayload(
  s: WalletApiSortState,
  fields: WalletApiSortPayloadFields = {}
): Record<string, 1 | -1> {
  const amountKey = fields.amountKey || "amount";
  const balanceKey = fields.balanceKey || "balance";

  if (s.key === "_id") {
    return { _id: s.dir };
  }
  if (s.key === "balance") {
    return { [balanceKey]: s.dir, _id: -1 };
  }

  // The API only sends one amount field. Credited/debited are derived from
  // `type`, so sort the zero-value side first for ascending and last for desc.
  if (s.key === "credited") {
    return {
      type: s.dir === 1 ? -1 : 1,
      [amountKey]: s.dir,
      _id: -1,
    };
  }
  return {
    type: s.dir === 1 ? 1 : -1,
    [amountKey]: s.dir,
    _id: -1,
  };
}

export type WalletTxSortKey = WalletApiSortKey;

export type CycleWalletSortFn = (field: WalletTxSortKey) => void;

const getWalletTxType = (type: any) => String(type || "").toLowerCase();

const getWalletAmount = (amount: any) => {
  const parsedAmount = Number(amount);
  return Number.isFinite(parsedAmount) ? parsedAmount : 0;
};

const isWalletCredit = (type: any) => getWalletTxType(type) === "credit";

const isWalletDebit = (type: any) => getWalletTxType(type) === "debit";

function SortChevron({
  active,
  dir,
}: {
  active: boolean;
  dir: 1 | -1;
}) {
  if (!active) {
    return (
      <span
        className="inline-block min-w-[12px] text-center text-xs font-semibold text-[#A4A4A4]"
        aria-hidden
      >
        ↕
      </span>
    );
  }
  return (
    <span
      className="text-xs font-semibold text-[#1C1C1C] min-w-[12px] text-center"
      aria-hidden
    >
      {dir === 1 ? "↑" : "↓"}
    </span>
  );
}

interface IPassbookProps {
  data: {
    title: string;
    rupee: string;
    date: string;
    credited: string;
    debited: string;
    balance: string;
    status: any;
    slabAmount: string;
    redeemAmount: string;
    redeemPoint: string;
    paymentGatewayName: any;
    privateCompanyId: any;
    remark: any;
    discription: any;
    orderId: any;
    amount: any;
    type: any;
  };
}

const columnsHelper = createColumnHelper<any>();

const PartialChecked = ({ checked, onChange, intermediate }: any) => {
  const ref: any = useRef(null);
  useEffect(() => {
    if (typeof intermediate === "boolean") {
      ref.current.indeterminate = intermediate;
    }
  }, [ref, intermediate]);
  return (
    <input
      type="checkbox"
      className="mr-3 cursor-pointer"
      ref={ref}
      checked={checked}
      onChange={onChange}
    />
  );
};

export const PassbookColumns = (opts: {
  apiSort: WalletApiSortState;
  cycleWalletSort: CycleWalletSortFn;
  onStatusFilterClick?: () => void;
  statusFilterActive?: boolean;
}) => {
  const {
    apiSort,
    cycleWalletSort,
    onStatusFilterClick,
    statusFilterActive,
  } = opts;

  const getStatusMeta = (status: string) => {
    const normalizedStatus = String(status || "N/A").toUpperCase();
    if (["SUCCESS", "APPROVED"].includes(normalizedStatus)) {
      return {
        label: normalizedStatus,
        icon: bookedIcon,
        wrapperClass: "bg-[#F2FAEF] border-[#7CCA62]",
        textClass: "text-[#7CCA62]",
      };
    }

    return {
      label: normalizedStatus,
      icon: PendingIcon,
      wrapperClass: "bg-[#FEEEEB] border-[#F35838]",
      textClass: "text-[#F35838]",
    };
  };

  const renderStatusComponent = (status: string) => {
    const statusMeta = getStatusMeta(status);
    return (
      <div>
        <div
          className={`inline-flex items-center text-xs justify-center gap-x-2 ${statusMeta.wrapperClass} rounded-sm border-[0.5px] px-3 py-[6px]`}
        >
          <img
            src={statusMeta.icon}
            alt=""
            width={20}
          />
          <span
            className={`text-xs font-semibold font-Open leading-5 ${statusMeta.textClass} items-center`}
          >
            {statusMeta.label}
          </span>
        </div>
      </div>
    );
  };

  // const compareDates = (rowA: any, rowB: any) => {
  //   const dateA = new Date(rowA.values.createdAt);
  //   const dateB = new Date(rowB.values.createdAt);
  //   return dateA > dateB ? 1 : -1;
  // };
  const isValidJSON = (str: any) => {
    return /^[\],:{}\s]*$/.test(
      str
        ?.replace(/\\["\\\/bfnrtu]/g, "@")
        .replace(
          /"[^"\\\n\r]*"|true|false|null|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?/g,
          "]"
        )
        .replace(/(?:^|:|,)(?:\s*\[)+/g, "")
    );
  };
  function formatEpochTimeWithAMPM(epochTime: number) {
    const date = new Date(epochTime);
    const formattedDate = date_DD_MMM_YYY(epochTime);

    const hours = date.getHours();
    const ampm = hours >= 12 ? "PM" : "AM";
    const formattedHours = hours % 12 === 0 ? 12 : hours % 12;

    const formattedTime = `${formattedHours}:${String(
      date.getMinutes()
    ).padStart(2, "0")}:${String(date.getSeconds()).padStart(2, "0")}`;

    return `${formattedDate} ${formattedTime} ${ampm}`;
  }

  return [
    columnsHelper.accessor("createdAt", {
      header: (props: any) => {
        return (
          <div className="flex justify-between items-center">
            <div className="flex justify-center items-center">
              {/* <PartialChecked
                checked={props.table?.getIsAllRowsSelected()}
                onChange={props?.table?.getToggleAllRowsSelectedHandler()}
                intermediate={props?.table?.getIsSomeRowsSelected()}
              /> */}
              <h1 className="font-Open font-semibold leading-5 text-sm">
                Date
              </h1>
            </div>
            <button
              type="button"
              className="flex items-center justify-center rounded p-0.5 hover:bg-[#F0F0F0]"
              onClick={() => cycleWalletSort("_id")}
              title="Toggle date order: newest ↔ oldest (_id)"
              aria-label="Sort by date"
            >
              <SortChevron
                active={apiSort.key === "_id"}
                dir={apiSort.dir}
              />
            </button>
          </div>
        );
      },

      cell: (info: any) => {
        // console.log("timestamp", info.getValue());
        const formattedDateTime = date_DD_MMM_YYYY_HH_MM(info.getValue());

        return (
          <div className="flex items-center">
            {/* <div className="flex items-center justify-center mr-3 cursor-pointer">
              <input
                type="checkbox"
                checked={info?.row?.getIsSelected()}
                onChange={info?.row?.getToggleSelectedHandler()}
              />
            </div> */}
            <div className="flex flex-col  whitespace-nowrap my-4 ">
              <span className="font-Open font-normal leading-5 text-sm">
                {formattedDateTime.split(",")[0]}
              </span>
              <span className="font-Open font-normal leading-5 text-sm">
                {formattedDateTime.split(",")[1]}
              </span>
            </div>
          </div>
        );
      },
    }),
    columnsHelper.accessor("transactionId", {
      header: () => {
        return (
          <div className="flex whitespace-nowrap justify-between items-center w-[90px] ">
            <h1 className="font-Open font-semibold leading-5 text-sm">
              Transaction ID
            </h1>
          </div>
        );
      },
      cell: (info: any) => {
        return (
          <div className="flex  items-center justify-between ">
            <div className=" w-[80px] whitespace-nowrap  overflow-hidden overflow-ellipsis font-Open font-normal leading-5 text-sm   ">
              {info.row.original.transactionId}
            </div>
            <div className="cursor-pointer">
              <CopyTooltip stringToBeCopied={info.row.original.transactionId} />
            </div>
          </div>
        );
      },
    }),
    columnsHelper.accessor("shipyaari_id", {
      header: () => {
        const companyName = COMPANY_NAME;
        return (
          <div className="flex justify-between items-center ">
            <div>
              <h1 className="font-Open font-semibold leading-5 text-sm">
                {capitalizeFirstLetter(companyName)} ID
              </h1>
            </div>
          </div>
        );
      },
      cell: (info: any) => {
        return (
          <div className="flex justify-center">
            {/* {info?.row?.original?.remark?.length === 0
              ? "---"
              : isValidJSON(info?.row?.original?.remark)
              ? JSON.parse(info?.row?.original?.remark)?.tempOrderId !==
                undefined
                ? JSON.parse(info?.row?.original?.remark)?.tempOrderId
                : "---"
              : "---"} */}
            {info?.row?.original?.tempOrderId}
          </div>
        );
      },
    }),
    columnsHelper.accessor("orderId", {
      header: () => {
        return (
          <div className="flex whitespace-nowrap justify-between items-center w-[90px]">
            <h1 className="font-Open font-semibold leading-5 text-sm ">
              Order ID
            </h1>
          </div>
        );
      },
      cell: (info: any) => {
        return (
          <div className="flex relative group w-[100px]">
            <span className=" flex w-[100px] font-Open font-normal leading-5 text-xs line-clamp-2 ">
              {info?.row?.original?.orderId}
            </span>
            <span className="text-lg w-20">
              {info?.row?.original?.orderId && (
                <CopyTooltip stringToBeCopied={info?.row?.original?.orderId} />
              )}
            </span>

            {info?.row?.original?.orderId?.length > 3 && (
              <div className="absolute left-0 line-clamp-3 top-full mt-1 w-[200px] p-2 bg-white text-gray-600 text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10">
                {info?.row?.original?.orderId}
              </div>
            )}
          </div>
        );
      },
    }),
    columnsHelper.accessor("tracking_no", {
      header: () => {
        return (
          <div className="flex justify-between items-center ">
            <div>
              <h1 className="font-Open font-semibold leading-5 text-sm ">
                Tracking No
              </h1>
            </div>
          </div>
        );
      },
      cell: (info: any) => {
        return (
          <div className="flex whitespace-nowrap justify-center ">
            {/* {info?.row?.original?.remark?.length === 0
              ? "---"
              : isValidJSON(info?.row?.original?.remark)
              ? JSON.parse(info?.row?.original?.remark)?.boxInfo?.[0]?.tracking
                  ?.awb !== undefined
                ? JSON.parse(info?.row?.original?.remark)?.boxInfo?.[0]
                    ?.tracking?.awb
                : "---"
              : "---"} */}
            {info?.row?.original?.awb}
          </div>
        );
      },
    }),
    columnsHelper.accessor(
      (row) => (isWalletCredit(row?.type) ? getWalletAmount(row?.amount) : 0),
      {
        id: "credited",
        header: () => {
          return (
            <button
              type="button"
              className="flex w-full min-w-[108px] items-center justify-between gap-1 rounded-md border border-transparent px-1 py-0.5 text-left hover:border-[#E8E8E8] hover:bg-[#FAFAFA]"
              onClick={() => cycleWalletSort("credited")}
              title="Sort credited amount: ascending ↔ descending"
              aria-label="Sort credited amounts"
            >
              <h1 className="font-Open text-sm font-semibold leading-5 text-[#2E7D32]">
                Credited
              </h1>
              <SortChevron
                active={apiSort.key === "credited"}
                dir={apiSort.key === "credited" ? apiSort.dir : 1}
              />
            </button>
          );
        },
        cell: (info: any) => {
          const isCredit = isWalletCredit(info?.row?.original?.type);
          const amount = getWalletAmount(info.getValue());
          const text = amount.toFixed(2);
          return (
            <div className="flex items-center font-Open text-sm leading-5 tabular-nums">
              {isCredit ? (
                <span className="font-medium text-[#2E7D32]">₹ {text}</span>
              ) : (
                <span className="text-[#9CA3AF]">₹ {text}</span>
              )}
            </div>
          );
        },
      }
    ),
    columnsHelper.accessor(
      (row) => (isWalletDebit(row?.type) ? getWalletAmount(row?.amount) : 0),
      {
        id: "debited",
        header: () => {
          return (
            <button
              type="button"
              className="flex w-full min-w-[100px] items-center justify-between gap-1 rounded-md border border-transparent px-1 py-0.5 text-left hover:border-[#E8E8E8] hover:bg-[#FAFAFA]"
              onClick={() => cycleWalletSort("debited")}
              title="Sort debited amount: ascending ↔ descending"
              aria-label="Sort debited amounts"
            >
              <h1 className="font-Open text-sm font-semibold leading-5 text-[#C62828]">
                Debited
              </h1>
              <SortChevron
                active={apiSort.key === "debited"}
                dir={apiSort.key === "debited" ? apiSort.dir : 1}
              />
            </button>
          );
        },
        cell: (info: any) => {
          const isDebit = isWalletDebit(info?.row?.original?.type);
          const amount = getWalletAmount(info.getValue());
          const text = amount.toFixed(2);
          return (
            <div className="flex items-center whitespace-nowrap font-Open text-sm leading-5 tabular-nums">
              {isDebit ? (
                <span className="font-medium text-[#C62828]">₹ {text}</span>
              ) : (
                <span className="text-[#9CA3AF]">₹ {text}</span>
              )}
            </div>
          );
        },
      }
    ),
    columnsHelper.accessor("balance", {
      header: () => {
        return (
          <button
            type="button"
            className="flex w-full min-w-[132px] items-center justify-between gap-1 rounded-md border border-transparent px-1 py-0.5 text-left hover:border-[#E8E8E8] hover:bg-[#FAFAFA]"
            onClick={() => cycleWalletSort("balance")}
            title="Toggle balance sort: ascending ↔ descending"
            aria-label="Sort by balance"
          >
            <h1 className="font-Open text-sm font-semibold leading-5 text-[#1C1C1C]">
              Balance
            </h1>
            <SortChevron
              active={apiSort.key === "balance"}
              dir={apiSort.dir}
            />
          </button>
        );
      },
      cell: (info: any) => {
        const raw = info.getValue();
        const n = parseFloat(String(raw ?? 0));
        const balance = Number.isFinite(n) ? n.toFixed(2) : "0.00";
        return (
          <div className="flex whitespace-nowrap font-Open text-sm font-medium leading-5 tabular-nums text-[#1C1C1C]">
            ₹ {balance}
          </div>
        );
      },
    }),

    columnsHelper.accessor("status", {
      header: () => {
        return (
          <div className="flex min-w-[118px] items-center justify-between gap-2">
            <div>
              <h1 className="font-Open font-semibold leading-5 text-sm">
                Status
              </h1>
            </div>
            {onStatusFilterClick && (
              <button
                type="button"
                className={`flex h-7 w-7 items-center justify-center rounded border ${
                  statusFilterActive
                    ? "border-[#004EFF] bg-[#F0F5FF]"
                    : "border-transparent hover:border-[#E8E8E8] hover:bg-[#FAFAFA]"
                }`}
                onClick={onStatusFilterClick}
                title="Filter by status"
                aria-label="Filter by status"
              >
                <img src={filterIconTable} alt="" className="h-4 w-4" />
              </button>
            )}
          </div>
        );
      },
      cell: (info: any) => {
        return renderStatusComponent(info.getValue());
      },
    }),

    columnsHelper.accessor("description", {
      header: () => {
        return (
          <div className="flex justify-between items-left">
            <div>
              <h1 className="font-Open font-semibold leading-5 text-sm">
                Description
              </h1>
            </div>
          </div>
        );
      },
      cell: (info: any) => {
        return (
          <div className="relative group w-[100px]">
            <span className="font-Open font-normal leading-5 text-xs line-clamp-2">
              {info?.row?.original?.description}
            </span>
            {info?.row?.original?.description?.length > 15 && (
              <div className="absolute left-0 line-clamp-3 top-full mt-1 w-[200px] p-2 bg-white text-gray-600 text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10">
                {info?.row?.original?.description}
              </div>
            )}
          </div>
        );
      },
    }),

    columnsHelper.accessor("userCount", {
      header: () => {
        return (
          <div className="flex justify-between">
            <h1 className="font-Open font-semibold leading-5 text-sm">
              Actions
            </h1>
          </div>
        );
      },
      cell: (info: any) => {
        return (
          <div className="flex  items-center justify-center">
            <div className="cursor-pointer">
              <CopyTooltip
                stringToBeCopied={`
                Date:${date_DD_MMM_YYY(info?.row?.original?.createdAt)},
                TransactionId:${info?.row?.original?.transactionId},
                ${
                  info?.row?.original?.type === "credit"
                    ? "Credited"
                    : "Debited"
                }:${info?.row?.original?.amount},Balance:${
                  info?.row?.original?.balance
                },
                TrackingNo:${info?.row?.original?.awb || "N/A"},
                ShipyaariId:${info?.row?.original?.tempOrderId || "N/A"},
                Status:${info?.row?.original?.status},
                Description:${info?.row?.original?.description}`}
              />
            </div>
          </div>
        );
      },
    }),
  ];
};

export const PassbookHistory: React.FunctionComponent<IPassbookProps> = ({
  data,
}: any) => {
  const [onOpen, setOnOpen] = useState(false);

  const copyString = ` Date:${date_DD_MMM_YYY(data?.date)}, 
                TransactionId:${data?.orderId},Credited:${
    data?.credited
  },Debited:${data?.debited},Balance:${data?.balance},Status:${
    data?.status
  },PrivateCompanyId:${data?.privateCompanyId},PaymentGatewayName:${
    data?.paymentGatewayName
  }`;

  const balance = data?.balance;
  let walletBalance = parseFloat(balance?.toFixed(2));

  const header = () => {
    return (
      <div
        className={`border-[1px] border-[#E8E8E8] rounded-lg overflow-hidden ${
          onOpen ? "grid grid-rows-1" : "grid h-[44px]"
        }`}
      >
        <div
          className={`flex justify-between items-center h-[44px] ${
            onOpen ? "bg-[#E8E8E8]" : "bg-white"
          }`}
        >
          <div
            className="max-w-[750px] max-sm:w-[200px] max-sm:truncate"
            title={data?.title}
          >
            <span className="text-base font-semibold text-[#1C1C1C] ml-4">
              {data?.title}
            </span>
          </div>
          <div className="flex items-center  space-x-3  mr-4">
            <div>
              <div
                className={`font-semibold ${
                  data.type === "credit" ? "text-[#7cca62]" : "text-[#f35838]"
                } `}
              >
                ₹ {data?.amount}
              </div>
              {/* <CustomButton
                text={data?.amount}
                onClick={() => { }}
                className={`p-2 bg-transparent ${data.type === "credit" ? "text-[#7cca62]" : "text-[#f35838]"}  h-[28px]`}
                showIcon={true}
                icon={RupeeIcon}
                iconClass={"w-[7px] mr-1"}
              /> */}
            </div>
            <div className="cursor-pointer">
              <img
                src={onOpen ? UpArrowIcon : DownArrowIcon}
                alt=""
                className="ml-1"
                onClick={() => setOnOpen(!onOpen)}
              />
            </div>
          </div>
        </div>

        <Collapsible open={onOpen} trigger="">
          <div className="flex flex-col ml-4 mt-4">
            <span className="font-Open font-semibold leading-5 text-sm">
              Date
            </span>
            <span className="text-xs text-[#1C1C1C] font-semibold">
              {date_DD_MMM_YYY(data?.date)}
            </span>
          </div>

          <div className="grid grid-cols-2 ml-4 mt-2">
            <div className="flex flex-col">
              <span className="text-[10px] text-[#95d47f]  font-semibold">
                Credited
              </span>
              <div className="flex items-center">
                <img src={RupeeIcon} alt="Rupee" className="mr-1 h-[10px]" />
                <span className="text-xs text-[#1C1C1C] font-semibold	">
                  {data?.credited}
                </span>
              </div>
            </div>

            <div className="flex flex-col border-[#E8E8E8] border-l-[1px]">
              <span className="ml-3 text-[10px] text-[#f35838] font-semibold">
                Debited
              </span>
              <span className="ml-3 flex text-xs text-[#1C1C1C] font-semibold items-center">
                <img src={RupeeIcon} alt="Rupee" className="mr-1 h-[10px]" />
                <span>{data?.debited}</span>
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 ml-4 mt-2">
            <div className="flex flex-col">
              <span className="text-[10px] text-[#777777] font-normal">
                Balance
              </span>
              <div className="flex items-center">
                <img src={RupeeIcon} alt="Rupee" className="mr-1 h-[10px]" />
                <span className="text-xs text-[#1C1C1C] font-semibold	">
                  {walletBalance}
                </span>
              </div>
            </div>

            <div className="flex flex-col border-[#E8E8E8] border-l-[1px]">
              <span className="ml-3 text-[10px] text-[#777777] font-normal">
                Status
              </span>
              <span className="ml-3 flex text-xs text-[#1C1C1C] font-semibold">
                {/* <img src={RupeeIcon} alt="Rupee" className="mr-1 h-[10px]" /> */}
                <span>{data?.status}</span>
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 ml-4 mt-2">
            <div className="flex flex-col">
              <span className="text-[10px] text-[#777777] font-normal">
                Transaction Id
              </span>
              <div className="flex items-center">
                <img src={RupeeIcon} alt="Rupee" className="mr-1 h-[10px]" />
                <span
                  className="text-xs text-[#1C1C1C] font-semibold max-sm:w-[120px] truncate"
                  title={data?.orderId}
                >
                  {data?.orderId}
                </span>
              </div>
            </div>

            <div className="flex flex-col border-[#E8E8E8] border-l-[1px]">
              <span className="ml-3 text-[10px] text-[#777777] font-normal">
                Remark
              </span>
              <span className="ml-3 mr-3 flex items-center max-w-full customScroll text-xs text-[#1C1C1C] font-semibold">
                {/* <img src={RupeeIcon} alt="Rupee" className="mr-1 h-[10px]" /> */}
                <span>{data?.remark}</span>
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 ml-4 mt-2">
            <div className="flex flex-col">
              <span className="text-[10px] text-[#777777] font-normal">
                Private Company Id
              </span>
              <div className="flex items-center">
                <img src={RupeeIcon} alt="Rupee" className="mr-1 h-[10px]" />
                <span className="text-xs text-[#1C1C1C] font-semibold	">
                  {data?.privateCompanyId}
                </span>
              </div>
            </div>
            <div className="flex flex-col border-[#E8E8E8] border-l-[1px]">
              <span className="ml-3 text-[10px] text-[#777777] font-normal">
                Payment Gateway Name
              </span>
              <span className="ml-3 flex text-xs text-[#1C1C1C] font-semibold">
                {/* <img src={RupeeIcon} alt="Rupee" className="mr-1 h-[10px]" /> */}
                <span>{data?.paymentGatewayName}</span>
              </span>
            </div>
          </div>

          <div className="flex mx-3 my-4">
            <div className="flex items-center cursor-pointer">
              <CopyTooltip stringToBeCopied={copyString} />

              <span className="ml-2 text-[#004EFF] text-sm">COPY</span>
            </div>
          </div>
        </Collapsible>
      </div>
    );
  };

  return <>{header()}</>;
};
